"use client";

import { useCallback, useEffect, useState } from "react";
import { workspacesApi } from "@/lib/api";
import {
  computeProfileMagicReadiness,
  type ProfileMagicReadiness,
  type ProfileMagicTagRow,
} from "@/lib/profileMagicReadiness";
import { loadProfileMagicTags } from "@/lib/profileMagicTags";

function unwrapWorkspaceName(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const o = data as { name?: string; data?: { name?: string }; workspace?: { name?: string } };
  const n =
    o.name?.trim() ||
    o.data?.name?.trim() ||
    o.workspace?.name?.trim() ||
    "";
  return n || null;
}

export function useProfileMagicWorkspace(workspaceId: string | null) {
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const [tags, setTags] = useState<ProfileMagicTagRow[]>([]);
  const [readiness, setReadiness] = useState<ProfileMagicReadiness | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!workspaceId) {
      setWorkspaceName(null);
      setTags([]);
      setReadiness(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [wsRaw, tagList] = await Promise.all([
        workspacesApi.getById(workspaceId).catch(() => null),
        loadProfileMagicTags(workspaceId),
      ]);
      const name = unwrapWorkspaceName(wsRaw);
      setWorkspaceName(name);
      setTags(tagList);
      setReadiness(
        computeProfileMagicReadiness({ workspaceName: name, tags: tagList }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "โหลดข้อมูลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const applyTags = useCallback(
    (next: ProfileMagicTagRow[]) => {
      setTags(next);
      setReadiness(
        computeProfileMagicReadiness({
          workspaceName,
          tags: next,
        }),
      );
    },
    [workspaceName],
  );

  return {
    workspaceName,
    tags,
    readiness,
    loading,
    error,
    refresh,
    applyTags,
    setWorkspaceName,
  };
}
